import React from "react";

import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import ErrorMessage from "@/ui/atoms/ErrorMessage";

import ErrorBoundary from "@/ui/organisms/ErrorBoundary";

import LayoutFullScreen from "@/ui/layouts/LayoutFullScreen";

import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";

import {
    ViewDatabaseGuard,
    ViewDealOverview,
    ViewTopBar,
    ViewExportModal,
    ViewNewExperienceSection,
    ViewNavigationSidebar,
} from "@/ui/compositionRootUI";

import {
    routerPaths,
    dealsOverviewTabsValidator,
} from "@/ui/router/routerPaths";

import { createProdaLinks } from "@/utils/createProdaLink";

import { useDragOver } from "./useDragOver";

const PageDealOverview = () => {
    const navigate = useNavigate();
    const { projectId, databaseId, tab } = useParams() as {
        projectId: string;
        databaseId: string;
        tab: string;
    };
    const location = useLocation();

    const isFileUploadModalOpen = location.pathname.includes("files/upload");

    React.useEffect(() => {
        const isValidTab = dealsOverviewTabsValidator(tab);
        if (isValidTab) return;
        navigate(
            routerPaths.projects.read({
                databaseId,
                projectId,
                tab: "documents",
            }),
        );
    }, [projectId, databaseId, navigate, tab]);

    const parsedProjectId = projectId ? parseInt(projectId, 10) : undefined;
    const parsedDatabaseId = databaseId ? parseInt(databaseId, 10) : undefined;
    const isValidIdTypeGuard = (value: unknown): value is number =>
        typeof value === "number" && !isNaN(value);

    const handleBack = () => {
        return navigate(routerPaths.main.default());
    };

    const handleAddFileButtonClicked = () => {
        navigate(routerPaths.files.upload.url());
    };

    const getExtractionUrl = ({
        projectId,
        databaseId,
        extractionId,
        extractionStatus,
    }: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => {
        return extractionStatus === "completed"
            ? routerPaths.extraction.review({
                  projectId: String(projectId),
                  databaseId: String(databaseId),
                  extractionId: String(extractionId),
              })
            : routerPaths.extraction.mappings({
                  projectId: String(projectId),
                  databaseId: String(databaseId),
                  extractionId: String(extractionId),
              });
    };

    const getRentRollExtractionUrl = ({
        fileId,
        extractionId,
        sheetId,
        databaseId,
    }: {
        fileId: number;
        extractionId: number;
        sheetId: string;
        databaseId: number;
    }): string => {
        return createProdaLinks(
            `/rr/upload/workbooks/${fileId}/worksheets/${sheetId}/rentrolls/${extractionId}/edit`,
            [databaseId],
        );
    };

    const handleViewExtractionButtonClick = (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => {
        navigate(getExtractionUrl(params));
    };

    const handleViewRentRollExtractionButtonClick = (params: {
        fileId: number;
        extractionId: number;
        sheetId: string;
        databaseId: number;
    }) => {
        window.open(getRentRollExtractionUrl(params), "_self");
    };

    const handleDetailButtonClicked = ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) => {
        navigate(
            routerPaths.projects.detail({
                databaseId: String(databaseId),
                projectId: String(projectId),
                tab: "properties",
            }),
        );
    };

    const handleActivityButtonClicked = ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) => {
        navigate(
            routerPaths.projects.activity({
                databaseId: String(databaseId),
                projectId: String(projectId),
            }),
        );
    };

    const dragHandlers = useDragOver({
        onDragEnter: () => {
            const uploadPath = routerPaths.files.upload.url();
            if (location.pathname.includes(uploadPath)) {
                return;
            }
            navigate(uploadPath);
        },
    });

    return (
        <LayoutFullScreen
            TopBarProps={{
                sx: {
                    backgroundColor: "var(--proda-white-110)",
                },
                onLogoClick: () => navigate(routerPaths.main.default()),
            }}
            MainContentProps={{
                sx: {
                    backgroundColor: "var(--proda-white)",
                    borderTop: "1px solid var(--primary-color)",
                },
            }}
            contentTopBar={<ViewTopBar />}
            contentTopBarBanner={<ViewNewExperienceSection />}
            contentSidebar={
                <ViewNavigationSidebar
                    enableUploadButton={true}
                    fileUploadModalOpen={isFileUploadModalOpen}
                    handleUploadFileButtonClicked={handleAddFileButtonClicked}
                />
            }
            {...dragHandlers}
        >
            <ErrorBoundary>
                <ViewDatabaseGuard>
                    {!isValidIdTypeGuard(parsedProjectId) ||
                    !isValidIdTypeGuard(parsedDatabaseId) ? (
                        <LayoutFillParentCentered>
                            <ErrorMessage>
                                The provided project ID or database ID is
                                invalid. Please check the URL and try again.
                            </ErrorMessage>
                        </LayoutFillParentCentered>
                    ) : (
                        dealsOverviewTabsValidator(tab) && (
                            <>
                                <ViewDealOverview
                                    projectId={parsedProjectId}
                                    databaseId={parsedDatabaseId}
                                    activeTab={tab}
                                    setActiveTab={targetTabName => {
                                        navigate(
                                            routerPaths.projects.read({
                                                databaseId,
                                                projectId,
                                                tab: targetTabName,
                                            }),
                                        );
                                    }}
                                    handleBackButtonClicked={handleBack}
                                    handleAddFileButtonClicked={
                                        handleAddFileButtonClicked
                                    }
                                    handleViewExtractionButtonClick={
                                        handleViewExtractionButtonClick
                                    }
                                    handleViewRentRollExtractionButtonClick={
                                        handleViewRentRollExtractionButtonClick
                                    }
                                    handleDetailButtonClicked={
                                        handleDetailButtonClicked
                                    }
                                    handleActivityButtonClicked={
                                        handleActivityButtonClicked
                                    }
                                    getExtractionUrl={getExtractionUrl}
                                    getRentRollExtractionUrl={
                                        getRentRollExtractionUrl
                                    }
                                    sx={{
                                        margin: "1rem 0",
                                    }}
                                    {...dragHandlers}
                                />
                                <ViewExportModal />
                                <Outlet />
                            </>
                        )
                    )}
                </ViewDatabaseGuard>
            </ErrorBoundary>
        </LayoutFullScreen>
    );
};

export default PageDealOverview;
